/**
 * Site geneli disk uyarısı (ADR-014).
 *
 * ## Neden ayrı bir bant?
 *
 * Katman bandı (`LayerBanner`) "içerik erişilemiyor" der. Bu bant ise
 * "**disk dolmak üzere**" der — yani oynatma *şu an* çalışıyor ama
 * birazdan çalışmayacak. İkisi farklı zaman ölçeklerinde uyarır ve
 * karışırsa kullanıcı hangisinin acil olduğunu anlamaz.
 *
 * ## Ne zaman görünür?
 *
 * Yalnız `warning` ve `critical` durumlarında. "Disk dolu" olmayan bir
 * sistemde sürekli bir uyarı bandı, bandı **görmezden gelmeyi** öğretir —
 * bu yüzden sade durumda hiçbir şey basılmaz.
 *
 * Ayrıntı (kalan alan, önbellek kullanımı) Hesap ekranındadır; burada tek
 * cümle yeter: kullanıcı oynatmaya devam edebilir mi, evet mi hayır mı?
 */
import type { ReactNode } from 'react';
import type { DiskStatus } from '../api.ts';

export function DiskBanner({
  status,
  updatedAt,
}: {
  status: DiskStatus | null;
  /** Son ölçüm zamanı. */
  updatedAt: number | null;
}): ReactNode {
  if (status === null) return null;
  if (status.state === 'ok') return null;

  const isCritical = status.state === 'critical';
  const free = status.filesystem?.freeBytes ?? null;

  return (
    <div
      className={`banner ${isCritical ? 'banner--danger' : 'banner--warn'}`}
      role="status"
      aria-live="polite"
    >
      <strong>{isCritical ? 'Disk dolmak üzere!' : 'Disk uyarısı:'}</strong>{' '}
      {status.messages.length > 0 ? (
        status.messages.join(' ')
      ) : (
        <>
          Sunucu diski dolmak üzere
          {free !== null ? ` (${formatBytes(free)} boş)` : ''}.
        </>
      )}
      {updatedAt !== null && (
        <span className="banner__hint">
          · Son ölçüm {new Date(updatedAt).toLocaleTimeString('tr-TR')} · Ayrıntı için Hesap
          ekranındaki "Disk Kullanımı" bölümüne bakın.
        </span>
      )}
    </div>
  );
}

/** Bayt → insan dili. */
function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const decimals = value >= 100 || unit === 0 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[unit]}`;
}
